import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, Send, Leaf } from 'lucide-react';

const STYLES = [
    { key: 'samtalepartner', emoji: '🌿', label_no: 'Samtalepartner', label_en: 'Companion' },
    { key: 'stottende', emoji: '💬', label_no: 'Støttende', label_en: 'Supportive' },
    { key: 'reflekterende', emoji: '🌱', label_no: 'Reflekterende', label_en: 'Reflective' },
    { key: 'coach', emoji: '🎯', label_no: 'Coach', label_en: 'Coach' },
    { key: 'strukturert', emoji: '🧠', label_no: 'Strukturert (KBT)', label_en: 'Structured (CBT)' },
    { key: 'stillroom', emoji: '🌫️', label_no: 'Stillroom', label_en: 'Stillroom' },
];

export default function Feedback({ standalone = false }) {
    const { user } = useAuth();
    const { lang } = useT();
    const navigate = useNavigate();
    const isEn = lang === 'en';

    const [nps, setNps] = useState(null);
    const [helpful, setHelpful] = useState(null);
    const [moodBefore, setMoodBefore] = useState(null);
    const [moodAfter, setMoodAfter] = useState(null);
    const [styles, setStyles] = useState([]);
    const [payWillingness, setPayWillingness] = useState(null);
    const [payTier, setPayTier] = useState(null);
    const [liked, setLiked] = useState('');
    const [disliked, setDisliked] = useState('');
    const [bugsOrIdeas, setBugsOrIdeas] = useState('');
    const [followupEmail, setFollowupEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const canSubmit = useMemo(
        () => nps !== null && helpful !== null,
        [nps, helpful]
    );

    const toggleStyle = (k) => {
        setStyles(prev => prev.includes(k) ? prev.filter(s => s !== k) : [...prev, k]);
    };

    const submit = async () => {
        if (!canSubmit) {
            toast.error(isEn ? 'NPS and helpfulness are required' : 'NPS og Hjalp-det-skår er påkrevd');
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/feedback', {
                nps, helpful, mood_before: moodBefore, mood_after: moodAfter,
                styles_used: styles,
                pay_willingness: payWillingness, pay_tier: payTier,
                liked, disliked, bugs_or_ideas: bugsOrIdeas,
                followup_email: followupEmail || null,
            });
            setDone(true);
            toast.success(isEn ? 'Thanks — feedback received.' : 'Takk! Tilbakemelding mottatt.');
        } catch (e) {
            toast.error(e.response?.data?.detail || (isEn ? 'Could not send' : 'Kunne ikke sende'));
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className={`${standalone ? 'min-h-screen bg-bone' : ''}`}>
                {standalone && <PublicHeader isEn={isEn}/>}
                <div className="max-w-2xl mx-auto px-6 py-12 text-center" data-testid="feedback-thanks">
                    <div className="w-14 h-14 rounded-full bg-sage-soft mx-auto flex items-center justify-center text-sage-dark">
                        <Check className="w-7 h-7"/>
                    </div>
                    <h1 className="font-display text-3xl font-bold mt-6 tracking-tight">
                        {isEn ? 'Thank you.' : 'Takk for hjelpen.'}
                    </h1>
                    <p className="text-inkSoft mt-3 leading-relaxed">
                        {isEn
                            ? 'Every piece of feedback shapes how Stillroom evolves.'
                            : 'Hver tilbakemelding former hvordan Stillerom utvikler seg videre.'}
                    </p>
                    <Button onClick={() => navigate(standalone ? '/' : '/app')} className="mt-8 rounded-full bg-sage hover:bg-sage-dark text-bone">
                        {isEn ? 'Back' : 'Tilbake til Stillerom'}
                    </Button>
                </div>
            </div>
        );
    }

    const helpfulOptions = isEn
        ? [{v:1,label:'No'},{v:2,label:'Slightly'},{v:3,label:'Somewhat'},{v:4,label:'A lot'},{v:5,label:'Very much'}]
        : [{v:1,label:'Nei'},{v:2,label:'Lite'},{v:3,label:'Noe'},{v:4,label:'Mye'},{v:5,label:'Veldig mye'}];

    const payOptions = isEn
        ? [{v:'yes',label:'Yes'},{v:'maybe',label:'Maybe'},{v:'no',label:'No'}]
        : [{v:'yes',label:'Ja'},{v:'maybe',label:'Kanskje'},{v:'no',label:'Nei'}];

    const tierOptions = isEn
        ? [{v:'basic',label:'Basic — $7/mo'},{v:'premium',label:'Premium — $14/mo'},{v:'none',label:'Neither'}]
        : [{v:'basic',label:'Basic – 99 kr/mnd'},{v:'premium',label:'Premium – 199 kr/mnd'},{v:'none',label:'Ingen av disse'}];

    return (
        <div className={`${standalone ? 'min-h-screen bg-bone' : ''}`}>
            {standalone && <PublicHeader isEn={isEn}/>}
            <div className="max-w-2xl mx-auto px-6 py-10 pb-24" data-testid="feedback-page">
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                {isEn ? 'Help us shape Stillroom' : 'Hjelp oss forme Stillerom'}
            </h1>
            <p className="text-inkSoft mt-3 leading-relaxed">
                {isEn
                    ? 'Thanks for testing with us. Your answers are only used to improve the app — no one outside the team sees them. Takes about 3 minutes.'
                    : 'Takk for at du tester med oss. Svarene dine brukes kun til å forbedre appen – ingen andre enn teamet ser dem. Det tar ca. 3 minutter.'}
            </p>

            {/* NPS */}
            <section className="mt-10" data-testid="section-nps">
                <h2 className="font-display text-lg font-semibold">
                    {isEn
                        ? '1. How likely are you to recommend Stillroom to someone you care about?'
                        : '1. Hvor sannsynlig er det at du ville anbefalt Stillerom til noen du bryr deg om?'}
                </h2>
                <p className="text-xs text-inkSoft mt-1">{isEn ? '0 = never · 10 = absolutely' : '0 = aldri · 10 = helt sikkert'}</p>
                <div className="mt-4 grid grid-cols-11 gap-1">
                    {Array.from({ length: 11 }, (_, i) => (
                        <button
                            key={i}
                            onClick={() => setNps(i)}
                            className={`h-10 rounded-lg text-sm font-medium transition-colors ${nps === i ? 'bg-sage text-bone border-2 border-sage-dark' : 'bg-white border border-sand text-ink hover:bg-sand/40'}`}
                            data-testid={`nps-${i}`}
                        >
                            {i}
                        </button>
                    ))}
                </div>
            </section>

            {/* Hjalp */}
            <section className="mt-10">
                <h2 className="font-display text-lg font-semibold">
                    {isEn ? '2. Did the conversations help with what you came for?' : '2. Hjalp samtalene deg med det du kom for?'}
                </h2>
                <div className="mt-4 grid grid-cols-5 gap-2">
                    {helpfulOptions.map(o => (
                        <button
                            key={o.v}
                            onClick={() => setHelpful(o.v)}
                            className={`p-3 rounded-xl text-sm font-medium transition-colors ${helpful === o.v ? 'bg-sage text-bone' : 'bg-white border border-sand text-ink hover:bg-sand/40'}`}
                            data-testid={`helpful-${o.v}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Mood */}
            <section className="mt-10">
                <h2 className="font-display text-lg font-semibold">
                    {isEn ? '3. Mood before and after (optional)' : '3. Humør før og etter (valgfritt)'}
                </h2>
                <p className="text-xs text-inkSoft mt-1">{isEn ? '1 = lowest · 5 = highest' : '1 = bunn · 5 = topp'}</p>
                <div className="mt-3 grid sm:grid-cols-2 gap-5">
                    <MoodRow label={isEn ? 'Before' : 'Før samtalen'} value={moodBefore} onChange={setMoodBefore} testidPrefix="mood-before"/>
                    <MoodRow label={isEn ? 'After' : 'Etter samtalen'} value={moodAfter} onChange={setMoodAfter} testidPrefix="mood-after"/>
                </div>
            </section>

            {/* Styles */}
            <section className="mt-10">
                <h2 className="font-display text-lg font-semibold">
                    {isEn ? '4. Which styles did you try?' : '4. Hvilke stiler prøvde du?'}
                </h2>
                <p className="text-xs text-inkSoft mt-1">{isEn ? 'Tick all that apply' : 'Kryss av alle som passer'}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                    {STYLES.map(s => {
                        const active = styles.includes(s.key);
                        return (
                            <button
                                key={s.key}
                                onClick={() => toggleStyle(s.key)}
                                className={`px-4 py-2 rounded-full text-sm border-2 transition-colors ${active ? 'border-sage bg-sage text-bone' : 'border-sand bg-white text-ink hover:border-sage/40'}`}
                                data-testid={`style-${s.key}`}
                            >
                                {s.emoji} {isEn ? s.label_en : s.label_no}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Free text */}
            <section className="mt-10 space-y-5">
                <FreeTextField
                    label={isEn ? '5. What was good?' : '5. Hva var bra?'}
                    value={liked}
                    onChange={setLiked}
                    testid="liked"
                    placeholder={isEn ? 'Something that landed right, a sentence that helped, a moment when the app made a difference …' : 'Noe som traff riktig, en setning som hjalp, en situasjon der appen gjorde en forskjell …'}
                />
                <FreeTextField
                    label={isEn ? '6. What was bad or frustrating?' : '6. Hva var dårlig eller frustrerende?'}
                    value={disliked}
                    onChange={setDisliked}
                    testid="disliked"
                    placeholder={isEn ? 'What felt wrong, stiff, awkward? Be honest — this is what we learn from most.' : 'Hva føltes feil, stivt, kleint? Vær ærlig – det er denne vi lærer mest av.'}
                />
                <FreeTextField
                    label={isEn ? '7. Bugs or concrete ideas?' : '7. Bugs eller konkrete ideer?'}
                    value={bugsOrIdeas}
                    onChange={setBugsOrIdeas}
                    testid="bugs"
                    placeholder={isEn ? 'Anything that crashed, looked off, or a feature you missed.' : 'Noe som krasjet, så rart ut, eller en funksjon du savnet.'}
                />
            </section>

            {/* Pay willingness */}
            <section className="mt-10">
                <h2 className="font-display text-lg font-semibold">
                    {isEn ? '8. Would you pay for Stillroom?' : '8. Ville du betalt for Stillerom?'}
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                    {payOptions.map(o => (
                        <button
                            key={o.v}
                            onClick={() => setPayWillingness(o.v)}
                            className={`px-5 py-2 rounded-full text-sm border-2 transition-colors ${payWillingness === o.v ? 'border-sage bg-sage text-bone' : 'border-sand bg-white text-ink hover:border-sage/40'}`}
                            data-testid={`pay-${o.v}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
                {payWillingness && payWillingness !== 'no' && (
                    <div className="mt-4">
                        <p className="text-sm text-inkSoft mb-2">{isEn ? 'Which plan would fit?' : 'Hvilken plan ville passet?'}</p>
                        <div className="flex flex-wrap gap-2">
                            {tierOptions.map(o => (
                                <button
                                    key={o.v}
                                    onClick={() => setPayTier(o.v)}
                                    className={`px-4 py-2 rounded-full text-sm border-2 transition-colors ${payTier === o.v ? 'border-sage bg-sage text-bone' : 'border-sand bg-white text-ink hover:border-sage/40'}`}
                                    data-testid={`tier-${o.v}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {/* Followup */}
            <section className="mt-10">
                <h2 className="font-display text-lg font-semibold">
                    {isEn ? '9. Follow-up (optional)' : '9. Oppfølging (valgfritt)'}
                </h2>
                <p className="text-xs text-inkSoft mt-1">
                    {isEn ? 'If you\u2019re open to a quick follow-up question by email' : 'Hvis du er åpen for at vi stiller et oppfølgingsspørsmål via e-post'}
                </p>
                <input
                    type="email"
                    value={followupEmail}
                    onChange={e => setFollowupEmail(e.target.value)}
                    className="mt-3 w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                    placeholder={isEn ? 'you@example.com' : 'din@epost.no'}
                    data-testid="followup-email"
                />
            </section>

            <div className="mt-12 flex items-center justify-between gap-4">
                <p className="text-xs text-inkSoft">
                    {user
                        ? (isEn ? <>Signed in as <span className="font-medium">{user.alias}</span>.</> : <>Logget inn som <span className="font-medium">{user.alias}</span>.</>)
                        : (isEn ? 'Filling out anonymously. No account required.' : 'Du fyller ut anonymt. Ingen konto kreves.')}
                </p>
                <Button
                    onClick={submit}
                    disabled={!canSubmit || submitting}
                    className="rounded-full bg-sage hover:bg-sage-dark text-bone px-6 h-11 shrink-0"
                    data-testid="submit-feedback-btn"
                >
                    <Send className="w-4 h-4 mr-2"/> {submitting ? (isEn ? 'Sending…' : 'Sender…') : (isEn ? 'Send feedback' : 'Send tilbakemelding')}
                </Button>
            </div>
            </div>
        </div>
    );
}

function PublicHeader({ isEn }) {
    return (
        <header className="max-w-2xl mx-auto px-6 py-6 flex items-center justify-between">
            <a href={isEn ? '/en' : '/'} className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-sage flex items-center justify-center">
                    <Leaf className="w-3.5 h-3.5 text-bone" />
                </div>
                <span className="font-display font-bold tracking-tight">{isEn ? 'Stillroom' : 'Stillerom'}</span>
            </a>
        </header>
    );
}

function MoodRow({ label, value, onChange, testidPrefix }) {
    return (
        <div>
            <p className="text-sm text-ink mb-2">{label}</p>
            <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                    <button
                        key={i}
                        onClick={() => onChange(i)}
                        className={`w-10 h-10 rounded-lg text-sm transition-colors ${value === i ? 'bg-sage text-bone border-2 border-sage-dark' : 'bg-white border border-sand text-ink hover:bg-sand/40'}`}
                        data-testid={`${testidPrefix}-${i}`}
                    >
                        {i}
                    </button>
                ))}
            </div>
        </div>
    );
}

function FreeTextField({ label, value, onChange, testid, placeholder }) {
    return (
        <div>
            <h2 className="font-display text-lg font-semibold">{label}</h2>
            <textarea
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                rows={3}
                maxLength={2000}
                className="mt-2 w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage resize-none"
                data-testid={`textarea-${testid}`}
            />
            <p className="text-[10px] text-inkSoft mt-1 text-right">{value.length}/2000</p>
        </div>
    );
}
